// src/components/PatientDetails.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, User, Phone, Mail, Calendar, MapPin,
  Syringe, CheckCircle, Clock, Plus, Edit, Trash2
} from 'lucide-react';
import './PatientDetails.css';

const PatientDetails = ({ patient, onBack, onAddVaccine, onEdit, onDelete, onEditVaccine, onAddNote }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [noteText, setNoteText] = useState('');

  const handleSaveNote = () => {
    if (noteText.trim()) {
      onAddNote(patient.id, noteText);
      setNoteText('');
    }
  };

  if (!patient) {
    return (
      <div className="patient-details">
        <div className="error-state">
          <User size={48} />
          <h3>Patient not found</h3>
          <button onClick={onBack} className="back-btn">
            Back to Patients
          </button>
        </div>
      </div>
    );
  }

  const completedVaccines = patient.vaccines?.filter(v => v.status === 'completed') || [];
  const pendingVaccines = patient.vaccines?.filter(v => v.status === 'pending') || [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      className="patient-details"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header with Back Button */}
      <motion.div className="details-header" variants={itemVariants}>
        <motion.button
          className="back-btn"
          onClick={onBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft size={20} />
          Back to Patients
        </motion.button>
        <h1>Patient Details</h1>
      </motion.div>

      {/* Patient Profile Card */}
      <motion.div className="profile-card" variants={itemVariants}>
        <div className="profile-header">
          <div className="profile-avatar large">
            {patient.name.split(' ').map(n => n[0]).join('')}
          </div>

          <div className="profile-info">
            <h2>{patient.name}</h2>
            <p className="mrn">{patient.mrn}</p>
            <div className="profile-stats">
              <div className="stat">
                <span className="stat-value">{patient.vaccines?.length || 0}</span>
                <span className="stat-label">Total Vaccines</span>
              </div>
              <div className="stat">
                <span className="stat-value">{completedVaccines.length}</span>
                <span className="stat-label">Completed</span>
              </div>
              <div className="stat">
                <span className="stat-value">{pendingVaccines.length}</span>
                <span className="stat-label">Pending</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="contact-info">
          <div className="contact-item">
            <Phone size={18} />
            <span>{patient.phone}</span>
          </div>
          <div className="contact-item">
            <Mail size={18} />
            <span>{patient.email || 'No email'}</span>
          </div>
          <div className="contact-item">
            <Calendar size={18} />
            <span>Age: {patient.age} • {patient.gender}</span>
          </div>
          {patient.dobNepali && (
            <div className="contact-item">
              <Calendar size={18} />
              <span>DOB (BS): {patient.dobNepali}</span>
            </div>
          )}
          {patient.fatherName && (
            <div className="contact-item">
              <User size={18} />
              <span>Father: {patient.fatherName}</span>
            </div>
          )}
          {patient.motherName && (
            <div className="contact-item">
              <User size={18} />
              <span>Mother: {patient.motherName}</span>
            </div>
          )}
          {patient.localAddress && (
            <div className="contact-item">
              <MapPin size={18} />
              <span>{patient.localAddress}</span>
            </div>
          )}
        </div>

        {/* Profile Actions - Bottom Right */}
        <div className="profile-actions-bottom">
          <motion.button
            className="action-btn-secondary edit"
            onClick={() => onEdit(patient)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit size={18} /> Edit
          </motion.button>
          <motion.button
            className="action-btn-secondary delete"
            onClick={() => onDelete(patient.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trash2 size={18} /> Delete
          </motion.button>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div className="tab-navigation" variants={itemVariants}>
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Vaccine History
        </button>
        <button
          className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Clinical Notes
        </button>
      </motion.div>

      {/* Tab Content */}
      <motion.div className="tab-content" variants={itemVariants}>
        {activeTab === 'overview' && (
          <div className="vaccine-history">
            <div className="section-header">
              <h3>Vaccine History</h3>
              <motion.button
                className="btn-primary"
                onClick={onAddVaccine}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus size={18} />
                Add Vaccine
              </motion.button>
            </div>

            {!patient.vaccines || patient.vaccines.length === 0 ? (
              <div className="empty-vaccines">
                <Syringe size={48} />
                <h4>No Vaccines Recorded</h4>
                <p>Add the first vaccine for this patient to get started</p>
                <motion.button
                  className="btn-primary"
                  onClick={onAddVaccine}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Plus size={18} />
                  Add First Vaccine
                </motion.button>
              </div>
            ) : (
              <div className="vaccine-list">
                {patient.vaccines.map((vaccine, index) => (
                  <motion.div
                    key={vaccine.id}
                    className={`vaccine-card ${vaccine.status}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="vaccine-icon">
                      <Syringe size={20} />
                    </div>
                    <div className="vaccine-info">
                      <h4>{vaccine.name}</h4>
                      <p>{vaccine.dose}</p>
                      <span className="vaccine-date">
                        Administered: {new Date(vaccine.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className={`vaccine-status ${vaccine.status}`}>
                      {vaccine.status === 'completed' ? (
                        <CheckCircle size={20} />
                      ) : (
                        <Clock size={20} />
                      )}
                      <span>{vaccine.status}</span>
                    </div>
                    <div className="vaccine-actions">
                      <button
                        className="action-btn edit"
                        onClick={() => onEditVaccine(vaccine)}
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="clinical-notes">
            <h3>Clinical Notes</h3>

            <div className="add-note-section" style={{ marginBottom: '20px' }}>
              <textarea
                placeholder="Add new clinical note..."
                className="note-textarea"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <button
                className="btn-primary"
                onClick={handleSaveNote}
                disabled={!noteText.trim()}
              >
                Add Note
              </button>
            </div>

            <div className="notes-list">
              {patient.notes && patient.notes.length > 0 ? (
                patient.notes.map(note => (
                  <div key={note.id} className="note-card">
                    <div className="note-header">
                      <span className="note-date">
                        {new Date(note.date).toLocaleDateString()} {new Date(note.date).toLocaleTimeString()}
                      </span>
                      <span className="note-author">{note.author}</span>
                    </div>
                    <div className="note-content">
                      <p>{note.text}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No clinical notes yet.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PatientDetails;